import { Stack } from '@mui/material';
import { useForm } from 'react-typed-form';
import { useParams } from 'react-router';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import { FieldSelect, FieldTextRequired } from '../../form';

type Props = {
  existing?: Api.DailyTip.V_ProgramWeekSub;
};

export default function DailyTipForm({ existing }: Props) {
  const callApi = useApiCallable();
  const { weekId } = useParams();

  const publishing = usePublishing(
    {
      contentType: 'Daily Tip',
      apiPath: 'daily-tips',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const initialValues = {
    title: existing?.title ?? '',
    day: existing?.day?.toString() ?? '1',
    description: existing?.description ?? '',
    week: `/program-weeks/${weekId}`,
  };

  const form = useForm<{
    title: string;
    day: string;
    description: string;
    week: string;
  }>({
    defaultValues: initialValues,
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      const submitValues = {
        ...values,
        day: parseInt(values.day, 10),
      };

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/daily-tips`,
          {
            jsonBody: submitValues,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/daily-tips/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: submitValues,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  return (
    <Stack gap={3}>
      <Stack gap={3} maxWidth={600}>
        <FieldTextRequired
          field={form.getField('title')}
          label="Title"
          helperText="The title will be displayed on the app home page"
        />
        <FieldSelect
          field={form.getField('day')}
          label="Day"
          options={[1, 2, 3, 4, 5, 6, 7].map((d) => ({
            label: d.toString(),
            value: d.toString(),
          }))}
          helperText="The day of the program week when this tip should be displayed."
          required
        />
        <FieldTextRequired
          field={form.getField('description')}
          label="Description"
          multiline
          minRows={4}
          helperText="The description is shown when the user opens the daily tip"
        />
      </Stack>
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Daily Tip"
        apiPath="daily-tips"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
